import { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Skeleton } from '../Skeleton';
import {
  OPENING_STAGES,
  OPENING_STAGE_HINTS,
  OPENING_STAGE_WORDS,
  hiringOpeningAPI,
  showMobile,
  type OpeningCandidateRow,
  type OpeningStage,
  type OpeningStageCounts,
} from '../../services/hiringOpeningApi';
import {
  HIRING_CODES,
  OPENING_WORDS,
  candidateRoundWords,
  candidateSourceWord,
  formatHiringShortDay,
  hiringCodeOf,
  hiringErrorText,
  isHiringOff,
} from './hiringText';

/**
 * The Candidates tab of one opening (approved mock-up, 15 Oct 2026; the table for everyone,
 * 27 Oct 2026).
 *
 * Everyone who may see hiring gets one page of ten candidates from the server, and this draws
 * them in a table - Name | Mobile | From | Source | Stage | Round | Applied - with a
 * name-or-mobile search, a stage filter (each word with how many the opening has at it) and
 * "Show more" to the last of them. From is the city the candidate lives in; Source is where
 * the application came from, in short words (candidateSourceWord). The Round cell reads
 * "Round 1", "Round 3 · 21 Oct" or "Round 1 done", with a small grey line under it where the
 * server sends one ("Round 2 to schedule"; 21 Oct 2026). Until 27 Oct 2026 the table went to
 * a holder of "Raise & cancel hiring" only, and everyone else saw the counts; the product
 * owner then decided "Everyone who can see Hiring". Until the first answer comes, a quiet
 * placeholder.
 *
 * What is read is shown and never kept: not in the page's address, a log, analytics or the
 * browser's storage. The search (a name or a mobile number) is sent in the BODY of the read
 * (hiringOpeningApi), never in a URL.
 */

const SEARCH_WAIT_MS = 300;
/** A name (letters, spaces, . ' -) or a mobile number (digits, spaces, + ( ) -): what the server searches by. */
const NAME_TEXT = /^[\p{L} .'-]*$/u;
const NUMBER_TEXT = /^[\d\s+().-]*$/;
const NAME_MAX = 50;
const MUTED_STAGES: readonly OpeningStage[] = ['rejected', 'withdrew'];

/**
 * The table fits the opening's window (HiringPanel, Modal "xl", 64rem since 27 Oct 2026; its
 * body about 61rem, a card of a store with several openings about 59rem) from a laptop's
 * width: its columns are fixed - each as wide as its longest usual words ("90000 01234",
 * "Added by recruiter", "Scheduling", "Round 3 · 21 Sept" and the grey "Round 2 to schedule",
 * "14 Sept") - and Name takes what is left (about 18.5rem in the window). Name, From and
 * Source end in "…" with the whole text on hover; Round's own words stay on one line, with
 * its grey note under them. Narrower than 51rem (a phone) its box scrolls sideways, the
 * columns keep their width and Name keeps 8.75rem, as Link an opening's table does.
 */
const TABLE = 'w-full min-w-[51rem] table-fixed border-collapse text-[13px]';
const TH = 'whitespace-nowrap px-2 py-2';
const TD = 'px-2 py-2 align-top';
/** One cell of one line: "…" when it is too long, the whole of it on hover. */
const CUT = `${TD} truncate`;
/** The columns' widths, in the table's order; Name has none (the rest of the width). */
const WIDTH = {
  name: '',
  mobile: 'w-[6.5rem]',
  from: 'w-[9rem]',
  source: 'w-[8.5rem]',
  stage: 'w-[5.75rem]',
  round: 'w-[8.5rem]',
  applied: 'w-[4rem]',
} as const;
const COLUMN_COUNT = Object.keys(WIDTH).length;

type Page = { rows: OpeningCandidateRow[]; total: number; nextOffset: number | null };

export function OpeningCandidates({ projectId, linkId, onCounts, onHiringGone, onNotListed }: {
  projectId: number;
  linkId: string;
  /** How many candidates the opening has, for the tab's own number. */
  onCounts?: (all: number) => void;
  onHiringGone: () => void;
  /** A read was refused: the opening is not in its budget's list (deleted in Recruitment). */
  onNotListed?: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [stage, setStage] = useState<OpeningStage | 'all'>('all');
  const [counts, setCounts] = useState<OpeningStageCounts | null>(null);
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reads, setReads] = useState(0);
  const owner = useRef({ onCounts, onHiringGone, onNotListed });
  owner.current = { onCounts, onHiringGone, onNotListed };
  const seq = useRef(0);

  // A name, or a number of 3 digits or more: anything else is not sent, and the line under the box says why.
  const isNumber = /\d/.test(typed);
  const nameOk = typed.trim().length <= NAME_MAX && (isNumber ? NUMBER_TEXT.test(typed) : NAME_TEXT.test(typed));
  // A number is searched from its third digit.
  const ready = nameOk && (!isNumber || typed.replace(/\D/g, '').length >= 3);
  useEffect(() => {
    if (!ready) return undefined;
    const next = typed.trim().replace(/\s+/g, ' ');
    const timer = setTimeout(() => setQ(next), next === q ? 0 : SEARCH_WAIT_MS);
    return () => clearTimeout(timer);
  }, [typed, ready, q]);

  useEffect(() => {
    seq.current += 1;
    const n = seq.current;
    const control = typeof AbortController === 'function' ? new AbortController() : null;
    setLoading(true);
    setError(null);
    Promise.resolve()
      .then(() => hiringOpeningAPI.candidates(projectId, linkId, { q, stage }, control?.signal as AbortSignal))
      .then((res) => {
        if (n !== seq.current) return;
        const got = res?.data?.data;
        if (!got || !got.counts || !got.people) {
          setError(OPENING_WORDS.failed);
          setLoading(false);
          return;
        }
        setCounts(got.counts);
        owner.current.onCounts?.(got.counts.all);
        setPage({ rows: got.people.rows, total: got.people.total, nextOffset: got.people.nextOffset });
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (n !== seq.current) return;
        setLoading(false);
        if (isHiringOff(err)) {
          owner.current.onHiringGone();
          return;
        }
        setError(hiringErrorText(err, OPENING_WORDS.failed));
        if (hiringCodeOf(err) === HIRING_CODES.notInBudget) owner.current.onNotListed?.();
      });
    return () => {
      control?.abort();
    };
  }, [projectId, linkId, q, stage, reads]);

  const showMore = () => {
    if (!page || page.nextOffset == null || more) return;
    const n = seq.current;
    setMore(true);
    Promise.resolve()
      .then(() => hiringOpeningAPI.candidates(projectId, linkId, { q, stage, offset: page.nextOffset ?? 0 }))
      .then((res) => {
        if (n !== seq.current) return;
        const people = res?.data?.data?.people;
        if (people) setPage((was) => ({ rows: [...(was?.rows ?? []), ...people.rows], total: people.total, nextOffset: people.nextOffset }));
      })
      .catch((err: unknown) => {
        if (n !== seq.current) return;
        if (isHiringOff(err)) {
          owner.current.onHiringGone();
          return;
        }
        setError(hiringErrorText(err, OPENING_WORDS.failed));
        if (hiringCodeOf(err) === HIRING_CODES.notInBudget) owner.current.onNotListed?.();
      })
      .finally(() => {
        if (n === seq.current) setMore(false);
      });
  };

  if (error && !counts) {
    return (
      <div className="space-y-1.5 text-sm">
        <p role="alert" className="text-gray-700">{error}</p>
        <button type="button" onClick={() => setReads((r) => r + 1)} className="text-[13px] font-medium text-primary-600 hover:underline">{OPENING_WORDS.tryAgain}</button>
      </div>
    );
  }

  // Not answered yet: a quiet placeholder.
  if (!counts && !error) {
    return (
      <div role="status" aria-label={OPENING_WORDS.loading} className="space-y-2.5">
        <Skeleton className="h-[34px] w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const rows = page?.rows ?? [];
  const countOf = (key: OpeningStage | 'all'): string => {
    const word = key === 'all' ? OPENING_WORDS.allStages : OPENING_STAGE_WORDS[key];
    return counts ? OPENING_WORDS.stageCount(word, counts[key]) : word;
  };
  return (
    <div className="space-y-2.5">
      {/* On a phone the search box has the full width, the stage filter under it: nothing is cut. */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="flex h-[34px] min-w-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 sm:flex-1">
          <Search className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={OPENING_WORDS.search}
            aria-label={OPENING_WORDS.search}
            maxLength={NAME_MAX + 10}
            autoComplete="off"
            className="w-full min-w-0 border-0 p-0 text-[13px] focus:outline-none focus:ring-0"
          />
        </label>
        <select
          aria-label={OPENING_WORDS.stage}
          value={stage}
          onChange={(e) => setStage(e.target.value as OpeningStage | 'all')}
          className="h-[34px] w-full rounded-lg border border-gray-300 bg-white px-2.5 text-[13px] text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 sm:w-auto"
        >
          <option value="all">{countOf('all')}</option>
          {OPENING_STAGES.map((key) => <option key={key} value={key}>{countOf(key)}</option>)}
        </select>
      </div>
      {!nameOk && <p className="text-xs text-amber-800">{OPENING_WORDS.badSearch}</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className={TABLE}>
          <thead>
            <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500">
              <th scope="col" className={TH}>{OPENING_WORDS.columns.name}</th>
              <th scope="col" className={`${TH} ${WIDTH.mobile}`}>{OPENING_WORDS.columns.mobile}</th>
              <th scope="col" title={OPENING_WORDS.fromHint} className={`${TH} ${WIDTH.from}`}>{OPENING_WORDS.columns.from}</th>
              <th scope="col" title={OPENING_WORDS.sourceHint} className={`${TH} ${WIDTH.source}`}>{OPENING_WORDS.columns.source}</th>
              <th scope="col" className={`${TH} ${WIDTH.stage}`}>{OPENING_WORDS.columns.stage}</th>
              <th scope="col" className={`${TH} ${WIDTH.round}`}>{OPENING_WORDS.columns.round}</th>
              <th scope="col" className={`${TH} ${WIDTH.applied}`}>{OPENING_WORDS.columns.applied}</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0
              ? Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-t border-gray-100">
                  {Array.from({ length: COLUMN_COUNT }).map((__, j) => <td key={j} className={TD}><Skeleton className="h-4 w-full" /></td>)}
                </tr>
              ))
              : rows.map((row, i) => {
                const round = candidateRoundWords(row);
                const source = candidateSourceWord(row.source);
                return (
                  <tr key={`${i}:${row.name ?? ''}`} className="border-t border-gray-100">
                    <td title={row.name || undefined} className={`${CUT} text-gray-900`}>{row.name || OPENING_WORDS.none}</td>
                    <td className={`${TD} whitespace-nowrap tabular-nums`}>{showMobile(row.mobile)}</td>
                    <td title={row.from || undefined} className={`${CUT} ${row.from ? 'text-gray-900' : 'text-gray-400'}`}>{row.from || OPENING_WORDS.none}</td>
                    <td title={row.source ? source : undefined} className={`${CUT} ${row.source ? 'text-gray-700' : 'text-gray-400'}`}>{source}</td>
                    <td
                      title={row.stage ? OPENING_STAGE_HINTS[row.stage] : undefined}
                      className={`${TD} whitespace-nowrap ${row.stage && MUTED_STAGES.includes(row.stage) ? 'text-gray-500' : 'text-gray-900'}`}
                    >
                      {row.stage ? OPENING_STAGE_WORDS[row.stage] : OPENING_WORDS.none}
                    </td>
                    <td className={`${TD} whitespace-nowrap tabular-nums ${row.round == null ? 'text-gray-400' : ''}`}>
                      {round.line}
                      {round.note && <span className="block whitespace-normal text-xs text-gray-500">{round.note}</span>}
                    </td>
                    <td className={`${TD} whitespace-nowrap text-gray-500`}>{row.appliedOn ? formatHiringShortDay(row.appliedOn) : OPENING_WORDS.none}</td>
                  </tr>
                );
              })}
            {!loading && rows.length === 0 && (
              <tr className="border-t border-gray-100">
                <td colSpan={COLUMN_COUNT} className="px-2 py-3 text-gray-500">{q || stage !== 'all' ? OPENING_WORDS.noMatch : OPENING_WORDS.noCandidates}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      {page && rows.length > 0 && (
        <div className="flex items-center justify-between text-[13px] text-gray-500">
          <span>{OPENING_WORDS.shown(rows.length, page.total)}</span>
          {page.nextOffset != null && (
            <button
              type="button"
              onClick={showMore}
              disabled={more}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {more && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {OPENING_WORDS.more}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default OpeningCandidates;
